// Tonight's score (SPEC §6.4). Factor values are computed by the api from facts; the score is
// Σ weight × value, here so the web can preview new weights with the same arithmetic.

export const FACTOR_KEYS = [
  'rested',
  'legacy',
  'away',
  'professionLag',
  'cooldowns',
  'quests',
  'mail',
] as const;
export type FactorKey = (typeof FACTOR_KEYS)[number];

export type Weights = Record<FactorKey, number>;

export const DEFAULT_WEIGHTS: Weights = {
  rested: 40,
  legacy: 40,
  away: 30,
  professionLag: 15,
  cooldowns: 10,
  quests: 10,
  mail: 5,
};

export const FACTOR_LABELS: Record<FactorKey, string> = {
  rested: 'Rested',
  legacy: 'Legacy',
  away: 'Away',
  professionLag: 'Profession lag',
  cooldowns: 'Cooldowns',
  quests: 'Quests',
  mail: 'Mail',
};

export interface FactorValue {
  key: FactorKey;
  /** 0–1; 0 when there's no data. */
  value: number;
  /** True when a fact the factor needs is missing ("no data" in the breakdown). */
  noData: boolean;
  /** What the value came from, e.g. "1.22 of 1.5 levels". */
  detail: string | null;
}

export interface ScoredFactor extends FactorValue {
  weight: number;
  points: number;
}

/** Applies weights: each factor's points, and the rounded score. */
export function scoreFactors(
  factors: FactorValue[],
  weights: Weights,
): { score: number; factors: ScoredFactor[] } {
  const scored = factors.map((f) => {
    const weight = weights[f.key] ?? 0;
    return { ...f, weight, points: weight * f.value };
  });
  return { score: Math.round(scored.reduce((s, f) => s + f.points, 0)), factors: scored };
}

/** The highest score the weights allow: the factor bar's full width. */
export function maxScore(weights: Weights): number {
  return FACTOR_KEYS.reduce((s, k) => s + (weights[k] ?? 0), 0);
}
